<template>
  <div class="relative w-full" ref="wrapRef">
    <!-- ใช้ Input ของคุณได้เลย -->
    <Input :label="label" :placeholder="placeholder" :warningMessage="warningMessage" :modelValue="innerValue"
      @update:modelValue="onInput" @focus="onFocus" @blur="onBlur" @keydown="onKeydown" />

    <!-- dropdown แยก Teleport ออกไปวางบน body -->
    <teleport to="body">
      <ul v-if="showList" class="auto_complete dropdown-portal" :class="{ 'no-border': isEmptyList }" :style="menuStyle"
        ref="listRef" @scroll="onScroll">
        <li v-for="(item, idx) in itemsView" :key="idx" @mousedown.prevent="pick(item)"
          :class="{ active: idx === activeIndex }">
          {{ displayText(item) }}
        </li>

        <!-- ว่างและไม่ได้โหลด -->
        <li v-if="!loading && !loadingMore && isEmpty && !hideNotFoundText" class="empty">
          {{ notFoundText }}
        </li>

        <!-- กำลังโหลดต่อ -->
        <li v-if="loadingMore" class="spinner"><span class="loader"></span></li>
      </ul>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

/**
 * คอมโพเนนต์กลางสำหรับอินพุตแบบ autocomplete
 * - ดึงข้อมูลผ่าน props.fetcher({ query, type, offset }) -> Promise<Array>
 * - รองรับ infinite scroll, debounce, คีย์บอร์ด, Teleport
 */

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  warningMessage: { type: String, default: '' },
  // ฟังก์ชันเรียกข้อมูล (ต้อง return Array เสมอ)
  // ex. ({query, type, offset}) => populationStore.autoCompletePopulationName(...)
  fetcher: { type: Function, required: true },

  // ส่งค่าพิเศษไปกับ fetcher ได้ (เช่น last_name จากช่องอีกอัน)
  extraParams: { type: Object, default: () => ({}) },

  // สำหรับกรณีต้องส่ง type ให้ API (เช่น 1=ชื่อ, 2=นามสกุล)
  type: { type: [Number, String], default: null },

  // map object -> string ที่จะแสดง
  // ใส่ key เป็นสตริง (เช่น "name") หรือฟังก์ชัน (item)=>string
  itemText: { type: [String, Function], default: 'name' },

  // map object -> value ที่จะใส่กลับไปใน v-model
  itemValue: { type: [String, Function], default: (v) => (typeof v === 'string' ? v : v?.name ?? '') },

  debounce: { type: Number, default: 300 },
  pageSize: { type: Number, default: 10 },
  hideNotFoundText: { type: Boolean, default: false },

  notFoundText: { type: String, default: 'ไม่พบข้อมูล' },
  /** ให้ยิง API ตอนค่าว่างบน focus */
  fetchOnFocusWhenEmpty: { type: Boolean, default: true },
  /** ให้ยิง API ตอนผู้ใช้ลบจนค่าว่าง */
  fetchWhenEmpty: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'select'])

const wrapRef = ref(null)
const listRef = ref(null)

const innerValue = ref(props.modelValue ?? '')
watch(() => props.modelValue, v => { innerValue.value = v ?? '' })

const showList = ref(false)
const items = ref([])
const offset = ref(0)
const lastBatchCount = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const activeIndex = ref(-1)
const initialized = ref(false) 

const toArray = v => (Array.isArray(v) ? v : [])
const itemsView = computed(() => toArray(items.value))
const isEmpty = computed(() => itemsView.value.length === 0)

const displayText = (item) => {
  if (typeof props.itemText === 'function') return props.itemText(item)
  if (typeof item === 'string') return item
  return item?.[props.itemText] ?? ''
}

const getValue = (item) => {
  if (typeof props.itemValue === 'function') return props.itemValue(item)
  if (typeof item === 'string') return item
  return item?.[props.itemValue] ?? ''
}

function makeDebounce(fn, delay) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), delay)
  }
}

const debouncedLoad = makeDebounce(() => {
  offset.value = 0
  lastBatchCount.value = 0
  load(false, false)
}, props.debounce)

function onInput(v) {
  innerValue.value = v
  emit('update:modelValue', v)
  showList.value = true
  // ถ้าผู้ใช้ลบจนว่างและอนุญาตให้ยิงตอนค่าว่าง -> ยิงทันที (ไม่ต้องรอ debounce)
  if ((v ?? '') === '' && props.fetchWhenEmpty) {
    offset.value = 0
    lastBatchCount.value = 0
    load(false, false)
  } else {
    debouncedLoad()
  }
}

function onFocus() {
  showList.value = true
  offset.value = 0
  lastBatchCount.value = 0
  // ถ้าค่าว่างและไม่อยากยิงตอนโฟกัส -> ข้าม
  if ((innerValue.value ?? '') === '' && !props.fetchOnFocusWhenEmpty) return
  load(false, false)
}

function onBlur() {
  setTimeout(() => { showList.value = false }, 150)
}

function ensureVisible() {
  const el = listRef.value
  const idx = activeIndex.value
  const kids = el?.children
  if (!kids || typeof kids.length !== 'number' || idx < 0 || idx >= kids.length) return
  kids[idx]?.scrollIntoView?.({ block: 'nearest' })
}

function onKeydown(e) {
  const len = itemsView.value.length
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      if (!showList.value) onFocus()
      if (len) {
        activeIndex.value = (activeIndex.value + 1) % len
        ensureVisible()
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      if (len) {
        activeIndex.value = (activeIndex.value - 1 + len) % len
        ensureVisible()
      }
      break
    case 'Enter':
      if (!showList.value) return
      e.preventDefault()
      if (activeIndex.value >= 0 && activeIndex.value < len) {
        pick(itemsView.value[activeIndex.value])
      }
      break
    case 'Escape':
      e.preventDefault()
      showList.value = false
      break
  }
}

function pick(item) {
  const val = getValue(item)
  emit('update:modelValue', val)
  emit('select', item)
  showList.value = false
  items.value = []
}

async function load(append = false, isLoadMore = false) {
  if (loading.value) return
  loading.value = true
  if (isLoadMore) loadingMore.value = true

  try {
    const list = await props.fetcher({
      query: String(innerValue.value ?? ''),
      type: props.type,
      offset: offset.value,
      ...props.extraParams,
    })
    const arr = Array.isArray(list) ? list : []
    lastBatchCount.value = arr.length

    if (append) items.value.push(...arr)
    else items.value = arr

    if (!append) activeIndex.value = items.value.length ? 0 : -1

    initialized.value = true 
  } finally {
    loading.value = false
    loadingMore.value = false
    await nextTick()
    placeDropdown()
  }
}

function onScroll(e) {
  const el = e.target
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
  if (!nearBottom) return
  // if (lastBatchCount.value === props.pageSize && !loading.value) {
  //   offset.value += 100
  //   load(true, true)
  // }
  if (lastBatchCount.value === 100 && !loading.value) {
    offset.value += 100
    load(true, true)
  }
}

/* ---- positioning ---- */
const menuStyle = ref({})
function placeDropdown() {
  const host = wrapRef.value
  if (!host) return
  const rect = host.getBoundingClientRect()
  const gap = 0
  menuStyle.value = {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.bottom + gap}px`,
    width: `${rect.width}px`,
    maxHeight: '130px',
  }
}
function repositionAll() {
  if (showList.value) placeDropdown()
}


// มี <li> อะไรจะแสดงไหม?
const hasAnyLi = computed(() => {
  const hasItems = itemsView.value.length > 0
  const showsEmpty = !loading.value && !loadingMore.value && isEmpty.value && !props.hideNotFoundText
  const showsSpinner = loadingMore.value
  return hasItems || showsEmpty || showsSpinner
})

const isEmptyList = computed(() => {
    const noItem = itemsView.value.length === 0
  const notLoading = !loading.value && !loadingMore.value

  // ก่อนจะ initialized ให้ซ่อน border ไปก่อนเลย
  if (!initialized.value) return true

  // หลังจากเริ่มใช้งานแล้ว
  // - ถ้ามี item -> มี border
  // - ถ้าไม่มี และซ่อน notFoundText -> ไม่มี border
  return noItem && notLoading && props.hideNotFoundText
})

onMounted(() => {
  window.addEventListener('resize', repositionAll)
  window.addEventListener('scroll', repositionAll, true)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', repositionAll)
  window.removeEventListener('scroll', repositionAll, true)
})
</script>

<style scoped lang="scss">
.auto_complete {
  width: 100%;
  border: 1px solid transparent;
  border-radius: 4px;
  // max-height: 220px;
  overflow-y: auto;
  background: #fff;
  li {
    padding: 4px 8px;
    cursor: pointer;
    transition: all .3s;
  }

  li:hover {
    background: #FBEFF8;
  }

  li.active {
    background: #FBEFF8;
  }

  .spinner {
    display: flex;
    justify-content: center;
    padding: 8px;
    pointer-events: none;
  }

  .empty {
    padding: 8px;
    color: #6b7280;
  }
}

.dropdown-portal {
  position: fixed;
  z-index: 999999;
  left: 0;
  top: 0;
}

.loader {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #9b237b;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
/* ไม่มีข้อมูลหรือยังไม่ initialized -> เอา border ออก */
.auto_complete.no-border {
  border-color: transparent !important; /* ใช้ transparent แทน none เพื่อให้ transition ทำงาน */
}

/* มีข้อมูล -> แสดง border */
.auto_complete:not(.no-border) {
  border-color: #b7b2b5;
}
</style>
